// src/pages/rapim/RapimDetail.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Edit, Play, Square, Presentation, Download,
  Plus, Trash2, Users, Clock, MapPin, FileText,
  CheckCircle, MessageSquare, Loader2, Calendar, GripVertical,
  Target, AlertTriangle, TrendingUp, XCircle, Paperclip, Search,
} from 'lucide-react';
import { Button, Badge, Card, Breadcrumb, Modal, Textarea, Input, ProgressBar, Select } from '../../components/ui';
import { TipTapEditor } from '../../components/ui/TipTapEditor';
import { SortableAgendaList, AgendaItem } from '../../components/rapim';
import KpiCard from '../../components/monitoring/KpiCard';
import toast from 'react-hot-toast';
import { rapimService } from '../../services/rapim';
import { adminService } from '../../services/admin';
import { useRole } from '../../hooks/useRole';
import { RapatPimpinan, PpeppRekomendasi, PpeppCreatePayload, RapimAgendaItem, PpeppKpi, Unit } from '../../types';

interface AvailableLaporanItem {
  id: number;
  kode: string;
  judul: string;
  tingkat_risiko: 'rendah' | 'sedang' | 'tinggi' | 'kritis' | 'critical';
  unit?: { id: number; name: string } | null;
  rapim_ulang?: boolean;
  riwayat_ppepp?: {
    ppepp_id?: number;
    progress_terakhir?: number;
    hambatan?: string;
    alasan_kembali?: string;
  } | null;
}

const RapimDetail: React.FC = () => {
  const { id } = useParams();
  const role = useRole();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState<'agenda' | 'peserta' | 'laporan' | 'notulensi' | 'keputusan' | 'lampiran'>('agenda');
  const [showAddLaporan, setShowAddLaporan] = useState(false);
  const [showKeputusan, setShowKeputusan] = useState(false);
  const [selectedLaporanId, setSelectedLaporanId] = useState<number | null>(null);
  const [showAddAgenda, setShowAddAgenda] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [lastSavedNotulen, setLastSavedNotulen] = useState('');

  const [rapat, setRapat] = useState<RapatPimpinan | null>(null);
  const [keputusan, setKeputusan] = useState<PpeppRekomendasi[]>([]);
  const [agendaItems, setAgendaItems] = useState<RapimAgendaItem[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [unitUsers, setUnitUsers] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [notulenText, setNotulenText] = useState('');
  const [attendanceLoading, setAttendanceLoading] = useState<{ userId: number; hadir: boolean } | null>(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkAbsensi, setBulkAbsensi] = useState<Record<number, { status: 'belum' | 'hadir' | 'tidak'; keterangan: string }>>({});
  const [availableLaporan, setAvailableLaporan] = useState<AvailableLaporanItem[]>([]);
  const [selectedAvailableLaporanIds, setSelectedAvailableLaporanIds] = useState<number[]>([]);
  const [loadingAvailableLaporan, setLoadingAvailableLaporan] = useState(false);
  const [searchAvailableLaporan, setSearchAvailableLaporan] = useState('');

  // Agenda form state
  const [agendaForm, setAgendaForm] = useState({
    title: '', description: '', type: 'pembahasan' as RapimAgendaItem['type'],
    laporan_masalah_id: undefined as number | undefined,
    duration_minutes: 15, order: 1,
  });

  // PPEPP Form State
  const [ppepp, setPpepp] = useState<Partial<PpeppCreatePayload>>({
    program: '', pengendalian: '', evaluasi: '',
    pengembangan: '', pelaporan: '',
    pic_unit_id: 0, pic_user_id: undefined, target_date: '', budget: 0,
  });
  const [ppeppPriority, setPpeppPriority] = useState<'rendah' | 'sedang' | 'tinggi' | 'kritis'>('sedang');

  const fetchData = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [rapatRes, kepRes, agendaRes] = await Promise.all([
        rapimService.getById(Number(id)),
        rapimService.getKeputusanByRapat(Number(id)),
        rapimService.getAgendaItems(Number(id)).catch(() => ({ data: [] })),
      ]);
      const rapatData = (rapatRes as any)?.data || rapatRes;
      setRapat(rapatData);
      setNotulenText(rapatData.notulen || '');

      const kepData = (kepRes as any)?.data || kepRes;
      setKeputusan(Array.isArray(kepData) ? kepData : (kepData?.data || []));

      const agData = (agendaRes as any)?.data || agendaRes;
      setAgendaItems(Array.isArray(agData) ? agData : (agData?.data || []));
    } catch (e) {
      console.error(e);
      toast.error('Gagal memuat detail rapat');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [id]);

  // Unit types excluded from PIC selection — leadership/policy level, bukan tim teknis
  const EXCLUDED_UNIT_TYPES = ['UNIVERSITAS', 'FAKULTAS', 'WAREK'];
  // Roles excluded from PIC selection — kebijakan, bukan pelaksana teknis
  const EXCLUDED_PIC_ROLES = ['rektor', 'dekan', 'wakil_rektor_1', 'wakil_rektor_2', 'wakil_rektor_3', 'wakil_rektor_4', 'warek', 'warek1', 'warek2', 'warek3', 'lpm'];

  useEffect(() => {
    const loadUnits = async () => {
      try {
        const res = await adminService.units.getAllFlat();
        const unitsData = (res as any)?.data || res;
        const allUnits = Array.isArray(unitsData) ? unitsData : (unitsData?.data || []);
        // Filter out leadership/policy-level units
        setUnits(allUnits.filter((u: any) => !EXCLUDED_UNIT_TYPES.includes((u.type || '').toUpperCase())));
      } catch (e) {
        console.error('Failed to load units', e);
      }
    };
    loadUnits();
  }, []);

  // Load users when unit PIC is selected
  useEffect(() => {
    const loadUnitUsers = async () => {
      if (!ppepp.pic_unit_id || ppepp.pic_unit_id <= 0) {
        setUnitUsers([]);
        return;
      }
      try {
        const res = await adminService.users.getAll({ unit_id: ppepp.pic_unit_id, per_page: 100 });
        const usersData = (res as any)?.data || res;
        const users = Array.isArray(usersData) ? usersData : (usersData?.data || []);
        // Exclude leadership roles from PIC selection
        const filteredUsers = users.filter((u: any) => {
          const roleName = (u.role?.name || '').toLowerCase();
          return !EXCLUDED_PIC_ROLES.includes(roleName);
        });
        setUnitUsers(filteredUsers.map((u: any) => ({ id: u.id, name: u.name })));
      } catch (e) {
        console.error('Failed to load unit users', e);
        setUnitUsers([]);
      }
    };
    loadUnitUsers();
  }, [ppepp.pic_unit_id]);

  const handleStartRapat = async () => {
    if (!rapat) return;
    setProcessing(true);
    try {
      await rapimService.startRapat(rapat.id);
      toast.success('Rapat dimulai');
      fetchData();
    } catch (e) { toast.error('Gagal memulai rapat'); }
    finally { setProcessing(false); }
  };

  const handleEndRapat = () => {
    if (!rapat) return;
    setShowEndModal(true);
  };

  const handleConfirmEndRapat = async () => {
    if (!rapat) return;
    setProcessing(true);
    try {
      await rapimService.endRapat(rapat.id);
      toast.success('Rapat selesai');
      setShowEndModal(false);
      fetchData();
    } catch (e) { toast.error('Gagal mengakhiri rapat'); }
    finally { setProcessing(false); }
  };

  const handleCancelRapat = async () => {
    if (!rapat || !cancelReason.trim()) {
      toast.error('Alasan pembatalan wajib diisi');
      return;
    }
    setProcessing(true);
    try {
      await rapimService.cancelRapat(rapat.id, cancelReason);
      toast.success('Rapat dibatalkan. Laporan yang belum dibahas dapat dijadwalkan kembali.');
      setShowCancelModal(false);
      setCancelReason('');
      fetchData();
    } catch (e) { toast.error('Gagal membatalkan rapat'); }
    finally { setProcessing(false); }
  };

  const canEditNotulen = role.canManageRapim;

  const handleAutoSaveNotulen = useCallback(() => {
    if (!rapat || !canEditNotulen || notulenText === lastSavedNotulen) return;
    rapimService.saveNotulen(rapat.id, notulenText)
      .then(() => {
        setLastSavedNotulen(notulenText);
        toast.success('Notulensi otomatis tersimpan', { icon: '💾' });
      })
      .catch(() => console.error('Auto-save failed'));
  }, [rapat, notulenText, lastSavedNotulen, canEditNotulen]);

  const handleReorderAgenda = async (items: AgendaItem[]) => {
    if (!rapat) return;
    setAgendaItems(items as RapimAgendaItem[]);
    try {
      await rapimService.reorderAgenda(rapat.id, items.map((item, index) => ({ id: item.id, order: index + 1 })));
    } catch (e) {
      toast.error('Gagal mengubah urutan agenda');
      fetchData();
    }
  };

  const handleSaveNotulen = async () => {
    if (!rapat) return;
    if (!canEditNotulen) {
      toast.error('Anda tidak memiliki izin untuk menyimpan notulensi');
      return;
    }
    setProcessing(true);
    try {
      await rapimService.saveNotulen(rapat.id, notulenText);
      toast.success('Notulensi berhasil disimpan');
    } catch (e) { toast.error('Gagal menyimpan notulensi'); }
    finally { setProcessing(false); }
  };

  const isBelumDicatat = (pivot: any) => {
    if (!pivot) return true;
    if (pivot.hadir) return false;
    if (pivot.keterangan_absensi) return false;
    if (!pivot.created_at || !pivot.updated_at) return false;
    return pivot.created_at === pivot.updated_at;
  };

  const handleAttendance = async (userId: number, hadir: boolean) => {
    if (!rapat) return;
    setAttendanceLoading({ userId, hadir });
    try {
      await rapimService.updateKehadiran(rapat.id, userId, hadir);
      // Optimistic update — update local state without refetching
      setRapat((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          peserta: prev.peserta?.map((p: any) =>
            p.id === userId
              ? { ...p, pivot: { ...p.pivot, hadir, updated_at: new Date().toISOString() } }
              : p
          ),
        };
      });
      toast.success('Kehadiran diperbarui', { icon: hadir ? '✅' : '❌' });
    } catch (e) {
      console.error(e);
      toast.error('Gagal menyimpan kehadiran');
    } finally {
      setAttendanceLoading(null);
    }
  };

  const buildBulkAbsensi = () => {
    const initial: Record<number, { status: 'belum' | 'hadir' | 'tidak'; keterangan: string }> = {};
    (rapat?.peserta || []).forEach((p) => {
      const belum = isBelumDicatat(p.pivot);
      initial[p.id] = {
        status: belum ? 'belum' : (p.pivot?.hadir ? 'hadir' : 'tidak'),
        keterangan: p.pivot?.keterangan_absensi || '',
      };
    });
    setBulkAbsensi(initial);
  };

  const handleBulkSave = async () => {
    if (!rapat) return;
    const pesertaAbsensi = (rapat.peserta || [])
      .map((p) => {
        const state = bulkAbsensi[p.id];
        if (!state) return null;
        const currentStatus = isBelumDicatat(p.pivot)
          ? 'belum'
          : (p.pivot?.hadir ? 'hadir' : 'tidak');
        if (state.status === 'belum' || state.status === currentStatus) {
          return null;
        }
        return {
          user_id: p.id,
          hadir: state.status === 'hadir',
          keterangan_absensi: state.keterangan || p.pivot?.keterangan_absensi || null,
        };
      })
      .filter(Boolean) as { user_id: number; hadir: boolean; keterangan_absensi?: string | null }[];

    if (pesertaAbsensi.length === 0) {
      toast.error('Tidak ada perubahan kehadiran');
      return;
    }

    setBulkSaving(true);
    try {
      await rapimService.updateAbsensiBulk(rapat.id, pesertaAbsensi);
      toast.success('Kehadiran berhasil disimpan');
      setBulkMode(false);
      setBulkAbsensi({});
      fetchData();
    } catch (e) {
      console.error(e);
      toast.error('Gagal menyimpan kehadiran');
    } finally {
      setBulkSaving(false);
    }
  };

  const openKeputusanModal = (laporanId: number) => {
    setSelectedLaporanId(laporanId);
    const lap = (rapat?.laporan_masalah || rapat?.laporan || [])?.find((l: any) => l.id === laporanId);
    // FULL RESET — clear all PPEPP fields so no stale data from previous decision
    setPpepp({
      laporan_masalah_id: laporanId,
      program: '',
      pengendalian: '',
      evaluasi: '',
      pengembangan: '',
      pelaporan: '',
      pic_unit_id: lap?.unit_id || 0,
      pic_user_id: undefined,
      target_date: '',
      budget: 0,
    });
    setUnitUsers([]); // Will be loaded by useEffect when pic_unit_id changes
    setPpeppPriority('sedang');
    setShowKeputusan(true);
  };

  const handleSaveKeputusan = async () => {
    if (!rapat || !selectedLaporanId) return;
    // ✅ ENHANCED: Validasi lebih ketat dengan pesan spesifik
    const errors: string[] = [];

    if (!ppepp.program || ppepp.program.trim().length < 5) {
      errors.push('Program minimal 5 karakter');
    }
    if (!ppepp.pengendalian || ppepp.pengendalian.trim().length < 5) {
      errors.push('Pengendalian minimal 5 karakter');
    }
    if (!ppepp.evaluasi || ppepp.evaluasi.trim().length < 5) {
      errors.push('Evaluasi minimal 5 karakter');
    }
    if (!ppepp.pengembangan || ppepp.pengembangan.trim().length < 5) {
      errors.push('Pengembangan minimal 5 karakter');
    }
    if (!ppepp.pelaporan || ppepp.pelaporan.trim().length < 5) {
      errors.push('Pelaporan minimal 5 karakter');
    }

    // ✅ CRITICAL: Validasi Unit PIC
    if (!ppepp.pic_unit_id || ppepp.pic_unit_id <= 0) {
      errors.push('Unit PIC WAJIB dipilih');
    }

    // ✅ CRITICAL: Validasi PIC User
    if (!ppepp.pic_user_id || ppepp.pic_user_id <= 0) {
      errors.push('Penanggung Jawab (PIC) WAJIB dipilih');
    }

    // ✅ CRITICAL: Validasi Target Date
    if (!ppepp.target_date) {
      errors.push('Target tanggal WAJIB diisi');
    }

    if (errors.length > 0) {
      toast.error(
        <div>
          <p className="font-bold mb-1">Validasi gagal:</p>
          <ul className="list-disc list-inside text-xs">
            {errors.map((err, i) => <li key={i}>{err}</li>)}
          </ul>
        </div>,
        { duration: 5000 }
      );
      return;
    }
    setProcessing(true);
    try {
      // ✅ FIXED: Pastikan type conversion benar
      const payload = {
        rapat_id: Number(id),
        laporan_masalah_id: selectedLaporanId,
        program: ppepp.program?.trim() || '',
        pengendalian: ppepp.pengendalian?.trim() || '',
        evaluasi: ppepp.evaluasi?.trim() || '',
        pengembangan: ppepp.pengembangan?.trim() || '',
        pelaporan: ppepp.pelaporan?.trim() || '',
        pic_unit_id: Number(ppepp.pic_unit_id), // ✅ Pastikan number
        pic_user_id: Number(ppepp.pic_user_id), // ✅ WAJIB
        target_date: ppepp.target_date || '',
        budget: Number(ppepp.budget) || 0,
        priority: ppeppPriority,
      };

      // ✅ DEBUG: Log payload sebelum submit
      console.log('📤 Submitting PPEPP:', payload);

      await rapimService.createKeputusan(payload as any);

      toast.success(
        <div>
          <p className="font-bold">✅ Keputusan PPEPP berhasil disimpan</p>
          <p className="text-xs mt-1">
            PIC: {units.find(u => u.id === payload.pic_unit_id)?.name}
            {payload.pic_user_id ? ` - ${unitUsers.find(u => u.id === payload.pic_user_id)?.name}` : ''}
          </p>
        </div>
      );
      setShowKeputusan(false);
      await rapimService.updateStatusPembahasan(rapat.id, selectedLaporanId, 'diputuskan');
      fetchData();
    } catch (e: any) {
      console.error(e);
      const errorMsg = e?.response?.data?.message ||
        e?.response?.data?.errors?.pic_unit_id?.[0] ||
        e?.response?.data?.errors?.target_date?.[0] ||
        'Gagal menyimpan keputusan';
      toast.error(errorMsg);
    }
    finally { setProcessing(false); }
  };

  // === AGENDA ITEMS ===
  const handleAddAgendaItem = async () => {
    if (!rapat || !agendaForm.title.trim()) {
      toast.error('Judul agenda wajib diisi');
      return;
    }
    setProcessing(true);
    try {
      await rapimService.createAgendaItem(rapat.id, {
        ...agendaForm,
        order: agendaItems.length + 1,
      });
      toast.success('Agenda berhasil ditambahkan');
      setShowAddAgenda(false);
      setAgendaForm({ title: '', description: '', type: 'pembahasan', laporan_masalah_id: undefined, duration_minutes: 15, order: 1 });
      fetchData();
    } catch (e) { toast.error('Gagal menambahkan agenda'); }
    finally { setProcessing(false); }
  };

  const handleDeleteAgendaItem = async (itemId: number) => {
    if (!rapat || !window.confirm('Hapus agenda ini?')) return;
    try {
      await rapimService.deleteAgendaItem(rapat.id, itemId);
      toast.success('Agenda dihapus');
      fetchData();
    } catch (e) { toast.error('Gagal menghapus agenda'); }
  };

  const handleOpenAddLaporan = async () => {
    if (!rapat) return;

    setShowAddLaporan(true);
    setSearchAvailableLaporan('');
    setSelectedAvailableLaporanIds([]);
    setLoadingAvailableLaporan(true);

    try {
      const res = await rapimService.getAvailableLaporan();
      const data = (res as any)?.data || res;
      const rows: AvailableLaporanItem[] = Array.isArray(data) ? data : (data?.data || []);
      const existingIds = new Set(
        ((rapat.laporan_masalah || rapat.laporan || []) as any[]).map((l: any) => Number(l.id))
      );

      const filteredRows = rows
        .filter((item) => !existingIds.has(Number(item.id)))
        .sort((a, b) => Number(Boolean(b.rapim_ulang)) - Number(Boolean(a.rapim_ulang)));

      setAvailableLaporan(filteredRows);
    } catch (e) {
      console.error(e);
      toast.error('Gagal memuat daftar laporan');
    } finally {
      setLoadingAvailableLaporan(false);
    }
  };

  const handleToggleAvailableLaporan = (laporanId: number) => {
    setSelectedAvailableLaporanIds((prev) => (
      prev.includes(laporanId)
        ? prev.filter((id) => id !== laporanId)
        : [...prev, laporanId]
    ));
  };

  const handleAddSelectedLaporan = async () => {
    if (!rapat) return;
    if (selectedAvailableLaporanIds.length === 0) {
      toast.error('Pilih minimal satu laporan');
      return;
    }

    setProcessing(true);
    try {
      await rapimService.addLaporan(rapat.id, selectedAvailableLaporanIds);
      toast.success(`${selectedAvailableLaporanIds.length} laporan berhasil ditambahkan`);
      setShowAddLaporan(false);
      setSelectedAvailableLaporanIds([]);
      setSearchAvailableLaporan('');
      fetchData();
    } catch (e: any) {
      const message = e?.response?.data?.message || 'Gagal menambahkan laporan';
      toast.error(message);
    } finally {
      setProcessing(false);
    }
  };

  const handleRemoveLaporan = async (laporanId: number) => {
    if (!rapat) return;
    if (!window.confirm('Hapus laporan ini dari agenda rapat?')) return;

    setProcessing(true);
    try {
      await rapimService.removeLaporan(rapat.id, laporanId);
      toast.success('Laporan dihapus dari rapat');
      fetchData();
    } catch (e: any) {
      const message = e?.response?.data?.message || 'Gagal menghapus laporan';
      toast.error(message);
    } finally {
      setProcessing(false);
    }
  };

  const exportPDF = async (template: 'resume' | 'hasil') => {
    if (!rapat) return;
    try {
      const arrayBuffer = await rapimService.exportPDF(rapat.id, template);
      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const filename = template === 'resume'
        ? `RESUME-RAPIM-${rapat.id}.pdf`
        : `KEPUTUSAN-RAPIM-${rapat.id}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export PDF Error:', e);
      toast.error('Gagal export PDF');
    }
  };

  const formatRupiahInput = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (!digits) return '';
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  if (loading || !rapat) return (
    <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-teal-main" /></div>
  );

  const laporanList = rapat.laporan_masalah || rapat.laporan || [];
  const selectedLaporan = selectedLaporanId
    ? (laporanList as any[]).find((l: any) => l.id === selectedLaporanId)
    : null;
  const riwayatPpeppSebelumnya = selectedLaporan?.riwayat_ppepp;
  const totalDuration = agendaItems.reduce((s, a) => s + a.duration_minutes, 0);
  const canManageLaporanRapim = role.canManageRapim && ['dijadwalkan', 'berlangsung'].includes(rapat.status);
  const filteredAvailableLaporan = availableLaporan.filter((item) =>
    item.kode.toLowerCase().includes(searchAvailableLaporan.toLowerCase())
    || item.judul.toLowerCase().includes(searchAvailableLaporan.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'RAPIM', href: '/rapim' },
        { label: 'Detail Rapat' },
      ]} />

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div>
          <Link to="/rapim" className="inline-flex items-center gap-1 text-sm text-neutral-text-secondary hover:text-teal-main mb-2">
            <ArrowLeft className="w-4 h-4" /> Kembali
          </Link>
          <h1 className="text-xl font-black text-teal-dark">{rapat.judul}</h1>
          <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-neutral-text-secondary">
            <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {new Date(rapat.tanggal).toLocaleString('id-ID')}</span>
            {rapat.tempat && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {rapat.tempat}</span>}
            <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {rapat.peserta?.length || 0} peserta</span>
            {totalDuration > 0 && <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {totalDuration} menit agenda</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {rapat.status === 'dijadwalkan' && role.canManageRapim && (
            <Button variant="success" size="sm" onClick={handleStartRapat} loading={processing}>
              Mulai Rapat
            </Button>
          )}
          {(rapat.status === 'dijadwalkan' || rapat.status === 'berlangsung') && role.canManageRapim && (
            <Button variant="outline" size="sm" onClick={() => setShowCancelModal(true)}>
              Batalkan Rapat
            </Button>
          )}
          {rapat.status === 'berlangsung' && (
            <>
              <Link to={`/rapim/${id}/presentasi`}>
                <Button variant="outline" size="sm">Presentasi</Button>
              </Link>
              {role.canManageRapim && (
                <Button variant="danger" size="sm" onClick={handleEndRapat} loading={processing}>
                  Akhiri Rapat
                </Button>
              )}
            </>
          )}
          {rapat.status !== 'dibatalkan' && (
            <>
              {(rapat.status === 'dijadwalkan' || rapat.status === 'berlangsung') && (
                <Button variant="outline" size="sm" onClick={() => exportPDF('resume')}>
                  Cetak Resume Pra-RAPIM
                </Button>
              )}
              {rapat.status === 'selesai' && (
                <Button variant="outline" size="sm" onClick={() => exportPDF('hasil')}>
                  Cetak Hasil RAPIM
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Status Banner */}
      <div className={`p-3 rounded-xl text-sm font-medium flex items-center justify-between ${rapat.status === 'dijadwalkan' ? 'bg-info/10 text-info' :
        rapat.status === 'berlangsung' ? 'bg-warning/10 text-warning' :
          rapat.status === 'dibatalkan' ? 'bg-red-100 text-red-700' :
            'bg-success/10 text-success'
        }`}>
        <span>
          {rapat.status === 'dijadwalkan' ? '📅 Dijadwalkan' :
            rapat.status === 'berlangsung' ? '🔴 Sedang Berlangsung' :
              rapat.status === 'dibatalkan' ? '❌ Dibatalkan' : '✅ Selesai'}
        </span>
        {rapat.status === 'dibatalkan' && rapat.alasan_pembatalan && (
          <span className="text-xs">Alasan: {rapat.alasan_pembatalan}</span>
        )}
        {rapat.status !== 'dibatalkan' && (
          <div className="flex items-center gap-4 text-xs">
            <span>{agendaItems.length} agenda</span>
            <span>{(laporanList as any[]).length} laporan</span>
            <span>{keputusan.length} keputusan</span>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-neutral-border overflow-x-auto">
        {[
          { id: 'agenda' as const, label: `Agenda (${agendaItems.length})`, icon: <Calendar className="w-4 h-4" /> },
          { id: 'peserta' as const, label: `Peserta (${rapat.peserta?.length || 0})`, icon: <Users className="w-4 h-4" /> },
          { id: 'laporan' as const, label: `Laporan (${(laporanList as any[]).length})`, icon: <FileText className="w-4 h-4" /> },
          { id: 'notulensi' as const, label: 'Notulensi', icon: <MessageSquare className="w-4 h-4" /> },
          { id: 'keputusan' as const, label: `Keputusan (${keputusan.length})`, icon: <CheckCircle className="w-4 h-4" /> },
          { id: 'lampiran' as const, label: `Lampiran (${rapat.attachments?.length || 0})`, icon: <Paperclip className="w-4 h-4" /> },
        ].map((tab) => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${activeTab === tab.id ? 'border-teal-main text-teal-main' : 'border-transparent text-neutral-text-secondary hover:text-neutral-text-primary'
              }`}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* ====== Tab: AGENDA (NEW — uses RapimAgendaItem) ====== */}
      {activeTab === 'agenda' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold text-teal-dark">Agenda Rapat</h3>
            {rapat.status === 'dijadwalkan' && role.canManageRapim && (
              <Button variant="primary" size="sm" onClick={() => setShowAddAgenda(true)} icon={<Plus className="w-4 h-4" />}>
                Tambah Agenda
              </Button>
            )}
          </div>

          {/* Structured agenda items */}
          {agendaItems.length > 0 ? (
            <SortableAgendaList
              items={agendaItems.map((item) => ({
                ...item,
                status: item.status || 'pending',
                laporanMasalah: item.laporan as any,
              }))}
              onReorder={handleReorderAgenda}
              onDelete={rapat.status === 'dijadwalkan' && role.canManageRapim ? handleDeleteAgendaItem : undefined}
            />
          ) : (
            <Card className="text-center py-8">
              <Calendar className="w-10 h-10 mx-auto mb-3 text-neutral-text-secondary" />
              <p className="text-sm font-medium">Belum ada agenda terstruktur</p>
              {rapat.agenda && (
                <div className="mt-4 text-left p-4 bg-neutral-bg rounded-xl">
                  <p className="text-xs font-semibold text-neutral-text-secondary mb-1">Agenda teks:</p>
                  <p className="text-sm whitespace-pre-wrap">{rapat.agenda}</p>
                </div>
              )}
            </Card>
          )}

          {/* Duration summary */}
          {agendaItems.length > 0 && (
            <div className="flex items-center justify-between text-xs text-neutral-text-secondary px-2">
              <span>{agendaItems.length} agenda items</span>
              <span>Total estimasi: <strong className="text-neutral-text-primary">{totalDuration} menit</strong></span>
            </div>
          )}
        </div>
      )}

      {/* Tab: Peserta */}
      {activeTab === 'peserta' && (
        <Card>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-base font-bold text-teal-dark">Daftar Peserta</h3>
              <p className="text-xs text-neutral-text-secondary">Catat kehadiran peserta rapat</p>
            </div>
            {(role.isAdmin || role.isRektor) && (
              <div className="flex items-center gap-2">
                {bulkMode ? (
                  <>
                    <Button variant="outline" size="sm" onClick={() => { setBulkMode(false); setBulkAbsensi({}); }}>
                      Batal
                    </Button>
                    <Button variant="primary" size="sm" onClick={handleBulkSave} loading={bulkSaving}>
                      Simpan Kehadiran
                    </Button>
                  </>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => { setBulkMode(true); buildBulkAbsensi(); }}>
                    Mode Massal
                  </Button>
                )}
              </div>
            )}
          </div>
          <div className="space-y-3">
            {rapat.peserta?.map((p, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-neutral-border last:border-0">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-teal-main text-white flex items-center justify-center text-xs font-bold">
                    {p.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-medium">{p.name}</p>
                    <p className="text-xs text-neutral-text-secondary">{p.role?.display_name || p.role?.name}</p>
                    {p.pivot?.keterangan_absensi && (
                      <p className="text-[11px] text-neutral-text-secondary mt-0.5">Keterangan: {p.pivot.keterangan_absensi}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {bulkMode ? (
                    <div className="w-44">
                      <Select
                        options={[
                          ...(isBelumDicatat(p.pivot) ? [{ value: 'belum', label: 'Belum Dicatat' }] : []),
                          { value: 'hadir', label: 'Hadir' },
                          { value: 'tidak', label: 'Tidak Hadir' },
                        ]}
                        value={bulkAbsensi[p.id]?.status || (isBelumDicatat(p.pivot) ? 'belum' : (p.pivot?.hadir ? 'hadir' : 'tidak'))}
                        onChange={(val) => setBulkAbsensi((prev) => ({
                          ...prev,
                          [p.id]: {
                            status: val as 'belum' | 'hadir' | 'tidak',
                            keterangan: prev[p.id]?.keterangan || p.pivot?.keterangan_absensi || '',
                          },
                        }))}
                      />
                    </div>
                  ) : (
                    <>
                      {(() => {
                        const belumDicatat = isBelumDicatat(p.pivot);
                        const statusLabel = belumDicatat ? 'Belum Dicatat' : (p.pivot?.hadir ? 'Hadir' : 'Tidak Hadir');
                        const statusClass = belumDicatat
                          ? 'text-neutral-text-secondary'
                          : (p.pivot?.hadir ? 'text-success' : 'text-danger');
                        return (
                          <span className={`text-xs font-semibold ${statusClass}`}>
                            {statusLabel}
                          </span>
                        );
                      })()}
                      {role.canManageRapim && rapat.status !== 'dibatalkan' && (
                        <div className="flex items-center gap-2">
                          <Button
                            variant={p.pivot?.hadir ? 'success' : 'outline'}
                            size="sm"
                            onClick={() => handleAttendance(p.id, true)}
                            loading={attendanceLoading?.userId === p.id && attendanceLoading?.hadir === true}
                          >
                            Hadir
                          </Button>
                          <Button
                            variant={!p.pivot?.hadir && !isBelumDicatat(p.pivot) ? 'danger' : 'outline'}
                            size="sm"
                            onClick={() => handleAttendance(p.id, false)}
                            loading={attendanceLoading?.userId === p.id && attendanceLoading?.hadir === false}
                          >
                            Tidak Hadir
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Tab: Laporan */}
      {activeTab === 'laporan' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold text-teal-dark">Daftar Laporan Rapat</h3>
            {canManageLaporanRapim && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleOpenAddLaporan}
                icon={<Plus className="w-4 h-4" />}
              >
                Tambah Laporan
              </Button>
            )}
          </div>
          {(laporanList as any[]).length === 0 && (
            <p className="text-center text-neutral-text-secondary py-8">
              Tidak ada laporan yang dibahas.
            </p>
          )}
          {(laporanList as any[]).map((lap: any, i: number) => (
            <Card key={i} hover>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-mono text-teal-main">{lap.kode}</span>
                    <Badge variant="risk" risk={lap.tingkat_risiko} />
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${lap.pivot?.status_pembahasan === 'diputuskan' ? 'bg-success/10 text-success' :
                      lap.pivot?.status_pembahasan === 'dibahas' ? 'bg-warning/10 text-warning' :
                        lap.pivot?.status_pembahasan === 'dibatalkan' ? 'bg-danger/10 text-danger' : 'bg-gray-100 text-gray-600'
                      }`}>
                      {lap.pivot?.status_pembahasan === 'diputuskan' ? 'Diputuskan' :
                        lap.pivot?.status_pembahasan === 'dibahas' ? 'Dibahas' : lap.pivot?.status_pembahasan === 'dibatalkan' ? 'Dibatalkan' : 'Menunggu'}
                    </span>
                  </div>
                  <p className="text-sm font-bold">{lap.judul}</p>
                  <p className="text-xs text-neutral-text-secondary mt-1">{lap.unit?.name}</p>
                </div>
                <div className="flex items-center gap-2">
                  {rapat.status !== 'dijadwalkan' && lap.pivot?.status_pembahasan !== 'diputuskan' && role.canManageRapim && (
                    <Button variant="primary" size="sm" onClick={() => openKeputusanModal(lap.id)}>
                      Buat Keputusan
                    </Button>
                  )}
                  {lap.pivot?.status_pembahasan === 'diputuskan' && (
                    <Button variant="ghost" size="sm" onClick={() => setActiveTab('keputusan')}>Lihat Keputusan</Button>
                  )}
                  {canManageLaporanRapim && lap.pivot?.status_pembahasan !== 'diputuskan' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveLaporan(lap.id)}
                      icon={<Trash2 className="w-4 h-4" />}
                    >
                      Hapus
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Tab: Notulensi */}
      {activeTab === 'notulensi' && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base font-bold text-teal-dark">Notulensi Rapat</h3>
              <p className="text-xs text-neutral-text-secondary mt-1">Auto-save setiap 60 detik</p>
            </div>
            {canEditNotulen && (
              <Button variant="primary" size="sm" onClick={handleSaveNotulen} loading={processing}>
                Simpan Manual
              </Button>
            )}
          </div>
          {!canEditNotulen && (
            <div className="mb-3 text-xs text-neutral-text-secondary">
              Notulensi hanya dapat diedit oleh rektor, wakil rektor, atau admin.
            </div>
          )}
          <TipTapEditor
            content={notulenText}
            onChange={setNotulenText}
            placeholder="Tulis notulensi rapat di sini..."
            minHeight="400px"
            editable={rapat.status !== 'dibatalkan' && canEditNotulen}
            autoSaveInterval={60000}
            onAutoSave={handleAutoSaveNotulen}
          />
        </Card>
      )}

      {/* Tab: Keputusan — enhanced with priority and KPI */}
      {activeTab === 'keputusan' && (
        <div className="space-y-4">
          {keputusan.length === 0 && <p className="text-center text-neutral-text-secondary py-8">Belum ada keputusan yang dibuat.</p>}
          {keputusan.map((k) => (
            <Card key={k.id} borderColor="border-l-success">
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-base font-bold text-teal-dark">{k.laporan?.judul}</h3>
                    <span className="text-xs font-mono text-teal-main">{k.laporan?.kode}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {k.priority && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase ${k.priority === 'kritis' ? 'bg-red-100 text-red-700' :
                        k.priority === 'tinggi' ? 'bg-orange-100 text-orange-700' :
                          k.priority === 'sedang' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                        }`}>{k.priority}</span>
                    )}
                    <Badge>{k.status || 'menunggu'}</Badge>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 p-4 bg-neutral-bg rounded-xl">
                  {[
                    { label: 'Program', value: k.program, color: 'border-l-teal-main' },
                    { label: 'Pengendalian', value: k.pengendalian, color: 'border-l-info' },
                    { label: 'Evaluasi', value: k.evaluasi, color: 'border-l-warning' },
                    { label: 'Pengembangan', value: k.pengembangan, color: 'border-l-success' },
                    { label: 'Pelaporan', value: k.pelaporan, color: 'border-l-purple-500' },
                  ].map((item, i) => (
                    <div key={i} className={`border-l-4 ${item.color} pl-3`}>
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary mb-1">{item.label}</p>
                      <p className="text-xs text-neutral-text-primary leading-relaxed">{item.value}</p>
                    </div>
                  ))}
                </div>
                {/* Meta row */}
                <div className="flex items-center gap-4 text-xs text-neutral-text-secondary">
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" /> PIC: {k.pic_user?.name || k.pic_unit?.name || '-'}</span>
                  <span className="flex items-center gap-1"><Target className="w-3 h-3" /> Target: {k.target_date ? new Date(k.target_date).toLocaleDateString('id-ID') : '-'}</span>
                  <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Budget: {k.budget ? new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(k.budget) : '-'}</span>
                </div>
                {/* Progress bar */}
                <ProgressBar value={k.progress_percentage || 0} color="teal" />

                {/* KPI cards if available */}
                {k.kpis && k.kpis.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary mb-2">Key Performance Indicators</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {k.kpis.map((kpi) => (
                        <KpiCard key={kpi.id} kpi={kpi} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Tab: Lampiran */}
      {activeTab === 'lampiran' && (
        <Card>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-teal-dark">Dokumen Pendukung</h3>
              {role.canManageRapim && rapat.status !== 'dibatalkan' && (
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={async (e) => {
                      const files = e.target.files;
                      if (!files || files.length === 0) return;
                      console.log('Files selected:', files.length);
                      try {
                        setProcessing(true);
                        for (const file of Array.from(files)) {
                          console.log('Uploading:', file.name);
                          await rapimService.uploadAttachment(Number(id), file);
                        }
                        toast.success(`${files.length} file berhasil diunggah`);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                        // Refresh data
                        const res = await rapimService.getById(Number(id));
                        setRapat((res as any).data || res);
                      } catch (err: any) {
                        console.error('Upload error:', err);
                        const msg = err?.response?.data?.message || err?.message || 'Gagal mengunggah file';
                        toast.error(msg);
                      } finally {
                        setProcessing(false);
                      }
                    }}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      console.log('Button clicked, canManageRapim:', role.canManageRapim, 'status:', rapat?.status);
                      fileInputRef.current?.click();
                    }}
                    disabled={processing}
                  >
                    {processing ? 'Mengunggah...' : 'Upload File'}
                  </Button>
                </div>
              )}
            </div>

            {rapat.attachments && rapat.attachments.length > 0 ? (
              <div className="space-y-2">
                {rapat.attachments.map((att: any) => (
                  <div
                    key={att.id}
                    className="flex items-center justify-between p-3 bg-neutral-bg rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <Paperclip className="w-5 h-5 text-neutral-text-secondary" />
                      <div>
                        <p className="text-sm font-medium">{att.original_name}</p>
                        <p className="text-xs text-neutral-text-secondary">
                          {(att.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <a
                        href={att.url || `/storage/${att.path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-teal-main text-sm hover:underline"
                      >
                        Download
                      </a>
                      {role.canManageRapim && (
                        <button
                          onClick={async () => {
                            if (!window.confirm('Hapus file ini?')) return;
                            try {
                              await rapimService.deleteAttachment(rapat.id, att.id);
                              toast.success('File dihapus');
                              fetchData();
                            } catch (err) {
                              toast.error('Gagal menghapus file');
                            }
                          }}
                          className="text-red-500 text-sm hover:underline"
                        >
                          Hapus
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-neutral-text-secondary">
                <Paperclip className="w-10 h-10 mx-auto mb-2 text-neutral-border" />
                <p>Belum ada dokumen pendukung</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Modal: Tambah Laporan */}
      <Modal
        isOpen={showAddLaporan}
        onClose={() => !processing && setShowAddLaporan(false)}
        title="Tambah Laporan ke Rapat"
        size="xl"
        footer={(
          <>
            <Button variant="outline" onClick={() => setShowAddLaporan(false)} disabled={processing}>
              Batal
            </Button>
            <Button variant="primary" onClick={handleAddSelectedLaporan} loading={processing}>
              Tambah Terpilih ({selectedAvailableLaporanIds.length})
            </Button>
          </>
        )}
      >
        <div className="space-y-4">
          <div className="p-3 bg-info/5 border border-info/20 rounded-xl text-sm text-info">
            Pilih laporan tervalidasi level 2 atau laporan RAPIM ulang yang belum masuk rapat aktif.
          </div>
          <Input
            placeholder="Cari laporan (kode / judul)..."
            value={searchAvailableLaporan}
            onChange={(e) => setSearchAvailableLaporan(e.target.value)}
            icon={<Search className="w-4 h-4" />}
          />

          {loadingAvailableLaporan ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-teal-main" />
            </div>
          ) : filteredAvailableLaporan.length === 0 ? (
            <div className="text-center text-sm text-neutral-text-secondary py-8">
              Tidak ada laporan yang bisa ditambahkan.
            </div>
          ) : (
            <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
              {filteredAvailableLaporan.map((item) => {
                const checked = selectedAvailableLaporanIds.includes(item.id);
                return (
                  <label
                    key={item.id}
                    className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer ${checked
                      ? 'bg-teal-main/5 border-teal-main/30'
                      : item.rapim_ulang
                        ? 'bg-amber-50 border-amber-200'
                        : 'bg-white border-neutral-border'
                      }`}
                  >
                    <input
                      type="checkbox"
                      className="mt-1 rounded border-neutral-border text-teal-main focus:ring-teal-light"
                      checked={checked}
                      onChange={() => handleToggleAvailableLaporan(item.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs font-mono text-teal-main">{item.kode}</span>
                        {item.rapim_ulang && (
                          <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">
                            RAPIM Ulang
                          </span>
                        )}
                        <Badge variant="risk" risk={item.tingkat_risiko === 'critical' ? 'kritis' : item.tingkat_risiko as any} />
                      </div>
                      <p className="text-sm font-semibold text-neutral-text-primary">{item.judul}</p>
                      <p className="text-xs text-neutral-text-secondary">{item.unit?.name || '-'}</p>
                      {item.rapim_ulang && (
                        <p className="text-xs text-amber-800 mt-1">
                          Alasan kembali: {item.riwayat_ppepp?.alasan_kembali || '-'}
                        </p>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </Modal>

      {/* ====== Modal: Add Agenda Item ====== */}
      <Modal
        isOpen={showAddAgenda}
        onClose={() => setShowAddAgenda(false)}
        title="📋 Tambah Agenda"
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowAddAgenda(false)}>Batal</Button>
            <Button variant="primary" onClick={handleAddAgendaItem} loading={processing}>Tambah</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="Judul Agenda"
            placeholder="Pembahasan laporan..."
            value={agendaForm.title}
            onChange={(e: any) => setAgendaForm({ ...agendaForm, title: e.target.value })}
            required
          />
          <Textarea
            label="Deskripsi (opsional)"
            placeholder="Detail pembahasan..."
            value={agendaForm.description}
            onChange={(e) => setAgendaForm({ ...agendaForm, description: e.target.value })}
            rows={3}
          />
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Tipe"
              options={[
                { value: 'pembahasan', label: 'Pembahasan' },
                { value: 'keputusan', label: 'Keputusan' },
                { value: 'informasi', label: 'Informasi' },
              ]}
              value={agendaForm.type}
              onChange={(v) => setAgendaForm({ ...agendaForm, type: v as RapimAgendaItem['type'] })}
            />
            <Input
              label="Durasi (menit)"
              type="number"
              value={agendaForm.duration_minutes.toString()}
              onChange={(e: any) => setAgendaForm({ ...agendaForm, duration_minutes: Number(e.target.value) })}
              min="5"
            />
          </div>
        </div>
      </Modal>

      {/* Modal: Keputusan PPEPP — FIXED dengan visual warning */}
      <Modal
        isOpen={showKeputusan}
        onClose={() => setShowKeputusan(false)}
        title="Buat Keputusan RAPIM (PPEPP)"
        size="xl"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowKeputusan(false)}>Batal</Button>
            <Button
              variant="primary"
              onClick={handleSaveKeputusan}
              loading={processing}
              icon={<CheckCircle className="w-4 h-4" />}
              // ✅ Disable jika validasi tidak terpenuhi
              disabled={!ppepp.pic_unit_id || !ppepp.target_date || !ppepp.program || !ppepp.pic_user_id}
            >
              Simpan Keputusan
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          {/* Info banner */}
          <div className="p-3 bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl border border-teal-200/60">
            <p className="text-xs font-bold text-teal-700 flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5" />
              Framework PPEPP (Program, Pengendalian, Evaluasi, Pengembangan, Pelaporan)
            </p>
          </div>

          {/* ✅ NEW: Warning Box jika data penting belum diisi */}
          {(!ppepp.pic_unit_id || !ppepp.target_date || !ppepp.pic_user_id) && (
            <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-xl animate-pulse">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-amber-800">
                    ⚠️ Perhatian: Data Wajib Belum Lengkap
                  </p>
                  <ul className="mt-2 space-y-1 text-xs text-amber-700">
                    {!ppepp.pic_unit_id && (
                      <li className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                        <strong>Unit PIC</strong> wajib dipilih
                      </li>
                    )}
                    {!ppepp.pic_user_id && (
                      <li className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                        <strong>Penanggung Jawab (PIC)</strong> wajib dipilih
                      </li>
                    )}
                    {!ppepp.target_date && (
                      <li className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                        <strong>Target Tanggal</strong> wajib diisi
                      </li>
                    )}
                  </ul>
                  <p className="mt-2 text-[11px] text-amber-600">
                    Data ini sangat penting untuk alur tindak lanjut dan monitoring.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Selected report preview */}
          {selectedLaporan && (
            <div className="flex items-start gap-3 p-3 bg-neutral-bg rounded-xl border border-neutral-border">
              <div className="p-2 rounded-lg bg-white border border-neutral-border flex-shrink-0">
                <FileText className="w-4 h-4 text-teal-main" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-mono text-teal-main">{selectedLaporan.kode}</p>
                <p className="text-sm font-bold text-neutral-text-primary truncate">{selectedLaporan.judul}</p>
                <p className="text-xs text-neutral-text-secondary mt-0.5">{selectedLaporan.unit?.name}</p>
              </div>
            </div>
          )}

          {riwayatPpeppSebelumnya && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-xs font-bold text-amber-800 mb-2 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" />
                Riwayat PPEPP Sebelumnya
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-amber-900">
                <div>
                  <p className="text-[10px] font-bold uppercase text-amber-700">Progres Terakhir</p>
                  <p className="font-semibold">{riwayatPpeppSebelumnya.progress_terakhir ?? 0}%</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase text-amber-700">Hambatan</p>
                  <p className="font-semibold">{riwayatPpeppSebelumnya.hambatan || '-'}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase text-amber-700">Alasan Kembali</p>
                  <p className="font-semibold">{riwayatPpeppSebelumnya.alasan_kembali || '-'}</p>
                </div>
              </div>
            </div>
          )}

          {/* ✅ FIXED: Prioritas + Unit PIC dengan visual feedback yang lebih jelas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Prioritas"
              options={[
                { value: 'rendah', label: '🟢 Rendah' },
                { value: 'sedang', label: '🟡 Sedang' },
                { value: 'tinggi', label: '🟠 Tinggi' },
                { value: 'kritis', label: '🔴 Kritis' },
              ]}
              value={ppeppPriority}
              onChange={(v) => setPpeppPriority(v as any)}
            />

            {/* ✅ FIXED: Unit PIC dengan visual feedback */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Unit PIC <span className="text-danger">*</span>
              </label>
              <select
                className={`
                  w-full rounded-lg px-4 py-2.5 text-sm bg-white
                  focus:outline-none focus:ring-2 transition-all
                  ${!ppepp.pic_unit_id
                    ? 'border-2 border-amber-400 bg-amber-50 focus:ring-amber-300 focus:border-amber-400'
                    : 'border border-neutral-border focus:ring-teal-light focus:border-teal-light'
                  }
                `}
                value={ppepp.pic_unit_id?.toString() || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setPpepp({
                    ...ppepp,
                    pic_unit_id: val ? parseInt(val, 10) : 0,
                    pic_user_id: undefined
                  });
                }}
              >
                <option value="">⚠️ Pilih unit penanggung jawab...</option>
                {units.map((u) => (
                  <option key={u.id} value={u.id.toString()}>{u.name}</option>
                ))}
              </select>
              {!ppepp.pic_unit_id && (
                <p className="text-xs text-amber-600 font-medium flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Wajib dipilih untuk menetapkan penanggung jawab
                </p>
              )}
            </div>
          </div>

          {/* ✅ FIXED: PIC User dropdown dengan visual feedback */}
          {ppepp.pic_unit_id && ppepp.pic_unit_id > 0 && (
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Penanggung Jawab (PIC) <span className="text-danger">*</span>
              </label>
              <select
                className={`
                  w-full rounded-lg px-4 py-2.5 text-sm bg-white
                  focus:outline-none focus:ring-2 transition-all
                  ${!ppepp.pic_user_id
                    ? 'border-2 border-amber-400 bg-amber-50 focus:ring-amber-300 focus:border-amber-400'
                    : 'border border-neutral-border focus:ring-teal-light focus:border-teal-light'
                  }
                `}
                value={ppepp.pic_user_id?.toString() || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setPpepp({ ...ppepp, pic_user_id: val ? parseInt(val, 10) : undefined });
                }}
              >
                <option value="">⚠️ Pilih penanggung jawab...</option>
                {unitUsers.map((u) => (
                  <option key={u.id} value={u.id.toString()}>{u.name}</option>
                ))}
              </select>
              {!ppepp.pic_user_id && unitUsers.length > 0 && (
                <p className="text-xs text-amber-600 font-medium flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Wajib dipilih untuk notifikasi & progress
                </p>
              )}
              {unitUsers.length === 0 && (
                <p className="text-xs text-neutral-text-secondary">
                  Tidak ada anggota unit yang tersedia. Seluruh unit akan bertanggung jawab.
                </p>
              )}
            </div>
          )}

          {/* PPEPP Fields — with colored left borders for visual grouping */}
          <div className="space-y-4">
            <p className="text-xs font-bold text-neutral-text-secondary uppercase tracking-widest">Detail Keputusan PPEPP</p>
            <div className="border-l-4 border-l-teal-main pl-4">
              <Textarea label="Program *" placeholder="Program yang direkomendasikan (min. 5 karakter)..." value={ppepp.program || ''} onChange={(e) => setPpepp({ ...ppepp, program: e.target.value })} required rows={3} />
            </div>
            <div className="border-l-4 border-l-blue-500 pl-4">
              <Textarea label="Pengendalian *" placeholder="Cara pengendalian pelaksanaan (min. 5 karakter)..." value={ppepp.pengendalian || ''} onChange={(e) => setPpepp({ ...ppepp, pengendalian: e.target.value })} required rows={3} />
            </div>
            <div className="border-l-4 border-l-amber-500 pl-4">
              <Textarea label="Evaluasi *" placeholder="Metode evaluasi keberhasilan (min. 5 karakter)..." value={ppepp.evaluasi || ''} onChange={(e) => setPpepp({ ...ppepp, evaluasi: e.target.value })} required rows={3} />
            </div>
            <div className="border-l-4 border-l-emerald-500 pl-4">
              <Textarea label="Pengembangan *" placeholder="Rencana pengembangan ke depan (min. 5 karakter)..." value={ppepp.pengembangan || ''} onChange={(e) => setPpepp({ ...ppepp, pengembangan: e.target.value })} required rows={3} />
            </div>
            <div className="border-l-4 border-l-purple-500 pl-4">
              <Textarea label="Pelaporan *" placeholder="Mekanisme pelaporan hasil (min. 5 karakter)..." value={ppepp.pelaporan || ''} onChange={(e) => setPpepp({ ...ppepp, pelaporan: e.target.value })} required rows={3} />
            </div>
          </div>

          {/* ✅ FIXED: Target + Budget dengan visual feedback */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-neutral-border">
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Target Tanggal <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className={`
                  w-full rounded-lg px-4 py-2.5 text-sm bg-white
                  focus:outline-none focus:ring-2 transition-all
                  ${!ppepp.target_date
                    ? 'border-2 border-amber-400 bg-amber-50 focus:ring-amber-300 focus:border-amber-400'
                    : 'border border-neutral-border focus:ring-teal-light focus:border-teal-light'
                  }
                `}
                value={ppepp.target_date || ''}
                onChange={(e) => setPpepp({ ...ppepp, target_date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
              />
              {!ppepp.target_date && (
                <p className="text-xs text-amber-600 font-medium flex items-center gap-1">
                  <Target className="w-3 h-3" />
                  Wajib diisi untuk monitoring deadline
                </p>
              )}
            </div>

            <Input
              label="Anggaran (Rp)"
              type="text"
              inputMode="numeric"
              placeholder="0"
              value={formatRupiahInput(ppepp.budget ? ppepp.budget.toString() : '')}
              onChange={(e: any) => {
                const digits = e.target.value.replace(/\D/g, '');
                setPpepp({ ...ppepp, budget: digits ? parseInt(digits, 10) : 0 });
              }}
              helpText="Opsional - estimasi biaya pelaksanaan"
            />
          </div>

          {/* ✅ NEW: Summary sebelum submit */}
          <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
            <p className="text-xs font-bold text-gray-700 mb-2">📋 Ringkasan Keputusan</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-gray-500">Unit PIC:</span>
                <span className={`ml-1 font-semibold ${ppepp.pic_unit_id ? 'text-success' : 'text-danger'}`}>
                  {ppepp.pic_unit_id ? units.find(u => u.id === ppepp.pic_unit_id)?.name : '❌ Belum dipilih'}
                </span>
              </div>
              <div>
                <span className="text-gray-500">PIC User:</span>
                <span className={`ml-1 font-semibold ${ppepp.pic_user_id ? 'text-success' : 'text-gray-400'}`}>
                  {ppepp.pic_user_id ? unitUsers.find(u => u.id === ppepp.pic_user_id)?.name : '(Seluruh unit)'}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Target:</span>
                <span className={`ml-1 font-semibold ${ppepp.target_date ? 'text-success' : 'text-danger'}`}>
                  {ppepp.target_date ? new Date(ppepp.target_date).toLocaleDateString('id-ID') : '❌ Belum diisi'}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Prioritas:</span>
                <span className="ml-1 font-semibold text-gray-700 capitalize">{ppeppPriority}</span>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Modal: Akhiri Rapat */}
      <Modal
        isOpen={showEndModal}
        onClose={() => !processing && setShowEndModal(false)}
        title="✅ Akhiri Rapat"
        size="md"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setShowEndModal(false)}
              disabled={processing}
            >
              Kembali
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirmEndRapat}
              loading={processing}
            >
              Ya, Akhiri Rapat
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
            <p className="text-sm font-semibold text-amber-800">
              Apakah Anda yakin ingin mengakhiri rapat ini?
            </p>
            <p className="text-sm text-amber-700 mt-1">
              Setelah diakhiri, rapat akan masuk status <strong>selesai</strong> dan tidak bisa dilanjutkan kembali.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg border border-neutral-border bg-neutral-bg px-3 py-2 text-center">
              <p className="text-[10px] uppercase font-semibold text-neutral-text-secondary">Agenda</p>
              <p className="text-sm font-bold text-neutral-text-primary">{agendaItems.length}</p>
            </div>
            <div className="rounded-lg border border-neutral-border bg-neutral-bg px-3 py-2 text-center">
              <p className="text-[10px] uppercase font-semibold text-neutral-text-secondary">Laporan</p>
              <p className="text-sm font-bold text-neutral-text-primary">{(laporanList as any[]).length}</p>
            </div>
            <div className="rounded-lg border border-neutral-border bg-neutral-bg px-3 py-2 text-center">
              <p className="text-[10px] uppercase font-semibold text-neutral-text-secondary">Keputusan</p>
              <p className="text-sm font-bold text-neutral-text-primary">{keputusan.length}</p>
            </div>
          </div>
        </div>
      </Modal>

      {/* Modal: Cancel Rapat */}
      <Modal
        isOpen={showCancelModal}
        onClose={() => {
          setShowCancelModal(false);
          setCancelReason('');
        }}
        title="❌ Batalkan Rapat"
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => {
              setShowCancelModal(false);
              setCancelReason('');
            }}>Batal</Button>
            <Button variant="danger" onClick={handleCancelRapat} loading={processing} disabled={!cancelReason.trim()}>
              Batalkan Rapat
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-sm text-red-700">
              <strong>Peringatan:</strong> Tindakan ini tidak dapat dibatalkan. Rapat yang dibatalkan tidak dapat dilanjutkan.
            </p>
            <p className="text-sm text-blue-700 mt-2">
              <strong>Catatan:</strong> Laporan yang belum dibahas akan dikembalikan statusnya dan dapat dijadwalkan di rapat lain.
            </p>
          </div>
          <Textarea
            label="Alasan Pembatalan"
            placeholder="Jelaskan alasan pembatalan rapat..."
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            required
            rows={4}
          />
        </div>
      </Modal>
    </div>
  );
};

export default RapimDetail;
